import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { RequirePermission } from "@/components/auth/RequirePermission";
import { SessionPage } from "@/components/session/SessionPage";
import { usePowtoonAuth } from "@/lib/auth/PowtoonAuthContext";
import { getOrgDomain, setOrgDomain } from "@/lib/powtoon-oauth";
import { setAuthIntent } from "@/lib/auth/postLoginDestination";

export function LearnerSessionByProject() {
  const { projectId = "" } = useParams();
  const [params] = useSearchParams();
  const urlOrgDomain = params.get("orgDomain");
  const publicLinkToken = params.get("public_link_token") ?? undefined;
  const isPublicLink = !!publicLinkToken;
  const {
    isLoading,
    isAuthenticated,
    orgDomain,
    signInWithPowtoon,
    signOut,
  } = usePowtoonAuth();
  const [tenantSwitching, setTenantSwitching] = useState(false);

  // Persist orgDomain from URL + remember destination for post-OAuth return.
  useEffect(() => {
    if (isPublicLink) return;
    if (urlOrgDomain) setOrgDomain(urlOrgDomain);
    if (projectId) setAuthIntent({ kind: "learner", projectId });
  }, [urlOrgDomain, projectId, isPublicLink]);

  // Tenant mismatch — force re-auth against the requested org.
  useEffect(() => {
    if (isPublicLink) return;
    if (isLoading || !isAuthenticated || !urlOrgDomain) return;
    const stored = getOrgDomain();
    if (stored && stored !== urlOrgDomain) {
      setTenantSwitching(true);
      signOut();
      signInWithPowtoon();
    }
  }, [isLoading, isAuthenticated, urlOrgDomain, signOut, signInWithPowtoon, isPublicLink]);

  const showLoader = useMemo(
    () =>
      !isPublicLink &&
      (isLoading || tenantSwitching || (!isAuthenticated && !!(urlOrgDomain || orgDomain))),
    [isPublicLink, isLoading, tenantSwitching, isAuthenticated, urlOrgDomain, orgDomain],
  );

  if (showLoader) {
    return (
      <div className="min-h-screen grid place-items-center text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Opening your training session…
        </div>
      </div>
    );
  }

  if (isPublicLink) {
    return (
      <SessionPage
        scenarioId={projectId}
        queryKey={["scenario-by-project", projectId, "public", publicLinkToken]}
        publicLinkToken={publicLinkToken}
        notFoundTitle="This avatar isn't available"
        notFoundMessage={`We couldn't find a live avatar for project ${projectId}. The link may have expired or been removed.`}
      />
    );
  }

  return (
    <RequirePermission permission="live-avatars.view">
      <SessionPage
        scenarioId={projectId}
        queryKey={["scenario-by-project", projectId]}
        notFoundTitle="This avatar isn't available"
        notFoundMessage={`We couldn't find a live avatar for project ${projectId}. It may have been removed or you may not have access.`}
      />
    </RequirePermission>
  );
}